import * as THREE from "../../libs/three.js/build/three.module.js";
import {ProfileViewSync, spriteWorldScale} from "./ProfileHoverMath.js";

// Markøren i 3D-vinduet for det punkt, musen peger på i 2D-profilen, samt den valgfri synkroniserede kamerafølge.
//
// Markøren er en konstant-pixel-ring (hul, med lille midterprik), der tegnes i viewerens overlay-pas (profileTool.scene,
// efter depth-clear): den ligger altid foran punktskyen, dækker ikke data (hul ring), og bliver ikke skjult af EDL/HQ-
// sammensætningen. Den gamle markør var en lille kugle i selve punktskyens scene (depth-testet, 10 px, MeshNormalMaterial),
// som punkterne dækkede, og som aldrig blev fjernet igen.
//
// Én og samme position (profilaksens xy ved punktets mileage, punktets kote) bruges til markør og kamera, så de aldrig er uenige.

const MARKER_PIXELS = 30;

function createMarkerTexture () {
	let size = 64;
	let canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	let ctx = canvas.getContext("2d");
	let c = size / 2;

	let ring = (radius, width, style) => {
		ctx.beginPath();
		ctx.arc(c, c, radius, 0, Math.PI * 2);
		ctx.lineWidth = width;
		ctx.strokeStyle = style;
		ctx.stroke();
	};

	// mørk kontur uden om og inden i den hvide ring, så den kan ses på både lys og mørk baggrund
	ring(24, 9, "rgba(0, 0, 0, 0.55)");
	ring(24, 5, "#ffffff");
	// lille midterprik i platformens accentfarve
	ctx.beginPath();
	ctx.arc(c, c, 6, 0, Math.PI * 2);
	ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
	ctx.fill();
	ctx.beginPath();
	ctx.arc(c, c, 4, 0, Math.PI * 2);
	ctx.fillStyle = "#00a3e6";
	ctx.fill();

	let texture = new THREE.CanvasTexture(canvas);
	texture.generateMipmaps = false;
	texture.minFilter = THREE.LinearFilter;

	return texture;
}

export class ProfileHover {
	constructor (viewer) {
		this.viewer = viewer;
		this.marker = null; // oprettes ved første brug
		this.visible = false;
		this.sync = new ProfileViewSync(
			() => this.viewer.scene.view,
			() => (this.viewer.scene.cameraP && this.viewer.scene.cameraP.fov) || 60
		);

		this._onUpdate = (e) => {
			this.sync.update(e.delta);
			if (this.visible) {
				this.updateMarkerScale();
			}
		};
		viewer.addEventListener("update", this._onUpdate);
	}

	ensureMarker () {
		if (this.marker) {
			return this.marker;
		}

		this.texture = createMarkerTexture();
		let material = new THREE.SpriteMaterial({
			map: this.texture,
			sizeAttenuation: false,
			depthTest: false,
			depthWrite: false,
			transparent: true
		});
		let sprite = new THREE.Sprite(material);
		sprite.name = "profile_hover_marker";
		sprite.renderOrder = 1000;
		sprite.frustumCulled = false;
		sprite.visible = false;
		// ingen interaktion: ProfileTool.scene er registreret som interaktiv scene i InputHandler (raycast), og en Sprite uden kamera ville kaste
		sprite.raycast = () => {};

		this.viewer.profileTool.scene.add(sprite);
		this.marker = sprite;

		return sprite;
	}

	updateMarkerScale () {
		let renderer = this.viewer.renderer;
		let height = renderer && renderer.domElement ? renderer.domElement.clientHeight : 0;
		let s = spriteWorldScale(MARKER_PIXELS, height, this.viewer.scene.getActiveCamera());
		if (s > 0) {
			this.marker.scale.set(s, s, 1);
		}
	}

	// position: {x, y, z} i verden; context: {length, width} (profilens længde og bredde, bruges til første zoom)
	show (position, context) {
		let marker = this.ensureMarker();
		marker.position.set(position.x, position.y, position.z);
		if (!this.visible) {
			this.visible = true;
			marker.visible = true;
		}
		this.updateMarkerScale();
		this.sync.setTarget(position, context);
	}

	// Intet gyldigt punkt (eller musen er væk): markøren skjules, kameraet står stille.
	hide () {
		if (this.visible) {
			this.visible = false;
			this.marker.visible = false;
		}
		this.sync.setTarget(null);
	}

	// Musen kommer ind over profilfladen
	enter () {
		this.sync.enter();
	}

	// Vinduet skjules/lukkes/minimeres eller profilen skiftes: markøren væk og følgen nulstilles (kameraet bliver, hvor det er)
	reset () {
		this.hide();
		this.sync.reset();
	}

	setSyncEnabled (on) {
		this.sync.setEnabled(on);
	}

	get syncEnabled () {
		return this.sync.enabled;
	}

	dispose () {
		this.viewer.removeEventListener("update", this._onUpdate);
		if (this.marker) {
			this.viewer.profileTool.scene.remove(this.marker);
			this.marker.material.dispose();
			this.texture.dispose();
			this.marker = null;
		}
		this.visible = false;
	}
}
